// Skjemaene i klubbportalen: klubb (navn, butikk, særidretter, avtale), person (trener,
// innkjøpsansvarlig, kontakt) og innlegg. Bare leder og klubbansvarlig ser dem.
import React, { useEffect, useState } from 'react';
import { Tekstfelt, Valgfelt, Valgknapper, knappPrimar, knappSekundar } from '../../ny-kundesamhandling/Skjema';
import VareLinjer from '../../tilbud/VareLinjer';
import { oppsummering, kr, prosent } from '../../tilbud/kalkyle';
import { BUTIKKER, ROLLER, klubbApi, butikkNavn } from './klubbApi';
import Prisoverslag from './Prisoverslag';
import { getArbeidsliste } from '../../lib/tjenestekatalogApi';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ramme = 'space-y-3 rounded-2xl border-2 border-indigo-200 bg-indigo-50/40 p-3';

function Feil({ children }) {
  if (!children) return null;
  return <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-red-800">{children}</p>;
}

function Knapper({ lagrer, onAvbryt, lagreTekst = 'Lagre' }) {
  return (
    <div className="flex flex-wrap gap-2">
      <button type="submit" disabled={lagrer} className={knappPrimar}>{lagrer ? 'Lagrer …' : lagreTekst}</button>
      <button type="button" onClick={onAvbryt} className={knappSekundar}>Avbryt</button>
    </div>
  );
}

// Ny klubb (klubb = null) eller endre klubb.
export function KlubbSkjema({ klubb = null, onLagret, onAvbryt }) {
  const [navn, setNavn] = useState(klubb?.navn || '');
  const [kortnavn, setKortnavn] = useState(klubb?.kortnavn || '');
  const [butikk, setButikk] = useState(klubb?.butikk || '');
  const [sarIdretter, setSarIdretter] = useState(klubb?.sarIdretter?.length ? klubb.sarIdretter : [{ navn: '' }]);
  const [avtaleId, setAvtaleId] = useState(klubb?.avtale?._id || '');
  const [info, setInfo] = useState(klubb?.info || '');
  const [klubbansvarlige, setKlubbansvarlige] = useState(klubb?.klubbansvarlige || []);
  const [aktiv, setAktiv] = useState(klubb ? klubb.aktiv !== false : true);
  const [avtaler, setAvtaler] = useState([]);
  const [ansatte, setAnsatte] = useState([]);
  const [lagrer, setLagrer] = useState(false);
  const [feil, setFeil] = useState('');

  useEffect(() => {
    fetch(`${API_BASE_URL}/avtaler`).then((r) => (r.ok ? r.json() : [])).then(setAvtaler).catch(() => {});
    fetch(`${API_BASE_URL}/employees`).then((r) => (r.ok ? r.json() : [])).then(setAnsatte).catch(() => {});
  }, []);

  const endreSarIdrett = (i, verdi) => setSarIdretter((l) => l.map((s, j) => (j === i ? { ...s, navn: verdi } : s)));
  // Bare ledere kan velges som klubbansvarlig hos oss (vinnsel, 02.11). De som allerede er valgt,
  // vises også, så de kan fjernes.
  const aktuelleAnsvarlige = ansatte.filter((a) => a.tilgang === 'leder' || klubbansvarlige.includes(a.navn));
  const veksleAnsvarlig = (n) =>
    setKlubbansvarlige((l) => (l.includes(n) ? l.filter((x) => x !== n) : [...l, n]));

  async function lagre(e) {
    e.preventDefault();
    if (!navn.trim()) return setFeil('Skriv inn navnet på klubben.');
    if (!butikk) return setFeil('Velg hvilken butikk klubben hører til.');
    setLagrer(true);
    setFeil('');
    try {
      const body = { navn, kortnavn, butikk, sarIdretter, avtaleId: avtaleId || null, info, klubbansvarlige, aktiv };
      const svar = klubb
        ? await klubbApi(`/klubber/${klubb._id}`, { method: 'PATCH', body })
        : await klubbApi('/klubber', { method: 'POST', body });
      onLagret(svar);
    } catch (err) {
      setFeil(err.message);
      setLagrer(false);
    }
  }

  const idPrefiks = klubb ? `kl-${klubb._id}` : 'kl-ny';
  const klubbavtaler = avtaler.filter((a) => a.type === 'KLUBB' || a._id === avtaleId);

  return (
    <form onSubmit={lagre} className={ramme} noValidate>
      <h2 className="text-lg font-bold text-slate-900">{klubb ? 'Endre klubben' : 'Ny klubb'}</h2>
      <div className="grid gap-3 sm:grid-cols-[2fr_1fr_1.5fr]">
        <Tekstfelt id={`${idPrefiks}-navn`} etikett="Navn" required value={navn} onChange={(e) => setNavn(e.target.value)} maxLength={100} />
        <Tekstfelt id={`${idPrefiks}-kort`} etikett="Kortnavn" value={kortnavn} onChange={(e) => setKortnavn(e.target.value)} maxLength={20} hjelp="F.eks. ROS eller HIF" />
        <Valgfelt id={`${idPrefiks}-butikk`} etikett="Butikk" value={butikk} onChange={(e) => setButikk(e.target.value)}>
          <option value="">Velg butikk</option>
          {BUTIKKER.map((b) => <option key={b.verdi} value={b.verdi}>{b.tittel}</option>)}
        </Valgfelt>
      </div>

      <fieldset className="min-w-0 space-y-2">
        <legend className="mb-1 font-semibold text-slate-800">Særidretter</legend>
        {sarIdretter.map((s, i) => (
          <div key={s._id || `ny-${i}`} className="flex flex-wrap items-center gap-2">
            <label htmlFor={`${idPrefiks}-sar-${i}`} className="sr-only">Særidrett {i + 1}</label>
            <input
              id={`${idPrefiks}-sar-${i}`}
              type="text"
              value={s.navn}
              onChange={(e) => endreSarIdrett(i, e.target.value)}
              maxLength={60}
              placeholder="F.eks. Fotball"
              className="min-w-0 flex-1 basis-48 rounded-xl border-2 border-slate-300 bg-white px-3 py-1.5 text-base focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600/30"
            />
            <button type="button" onClick={() => setSarIdretter((l) => l.filter((_, j) => j !== i))} className={knappSekundar}>
              Fjern<span className="sr-only"> {s.navn || `særidrett ${i + 1}`}</span>
            </button>
          </div>
        ))}
        <button type="button" onClick={() => setSarIdretter((l) => [...l, { navn: '' }])} className={knappSekundar}>
          + Legg til særidrett
        </button>
      </fieldset>

      <Valgfelt id={`${idPrefiks}-avtale`} etikett="Klubbavtale (fra Avtaler)" value={avtaleId} onChange={(e) => setAvtaleId(e.target.value)}>
        <option value="">Ingen avtale koblet</option>
        {klubbavtaler.map((a) => <option key={a._id} value={a._id}>{a.name}</option>)}
      </Valgfelt>

      <Tekstfelt
        id={`${idPrefiks}-info`}
        etikett="Fast informasjon om klubben"
        flerlinjet
        rows={4}
        value={info}
        onChange={(e) => setInfo(e.target.value)}
        maxLength={5000}
        hjelp="F.eks. fakturaadresse, hvem som godkjenner innkjøp, hvordan klubbtøy bestilles."
      />

      {aktuelleAnsvarlige.length > 0 && (
        <fieldset className="min-w-0">
          <legend className="mb-1 font-semibold text-slate-800">Klubbansvarlig hos oss</legend>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {aktuelleAnsvarlige.map((a) => (
              <label key={a._id} className="flex items-center gap-2">
                <input type="checkbox" className="h-5 w-5" checked={klubbansvarlige.includes(a.navn)} onChange={() => veksleAnsvarlig(a.navn)} />
                {a.navn}
              </label>
            ))}
          </div>
        </fieldset>
      )}

      {klubb && (
        <label className="flex items-center gap-2">
          <input type="checkbox" className="h-5 w-5" checked={aktiv} onChange={(e) => setAktiv(e.target.checked)} />
          Aktiv klubb (ikke aktive vises nederst i oversikten)
        </label>
      )}

      <Feil>{feil}</Feil>
      <Knapper lagrer={lagrer} onAvbryt={onAvbryt} lagreTekst={klubb ? 'Lagre endringene' : 'Opprett klubben'} />
    </form>
  );
}

// Ny person (person = null) eller endre person.
export function PersonSkjema({ klubb, person = null, standardRolle = 'trener', standardSarIdrett = '', onLagret, onAvbryt }) {
  const [felt, setFelt] = useState({
    navn: person?.navn || '',
    rolle: person?.rolle || standardRolle,
    sarIdrettId: person ? person.sarIdrettId || '' : standardSarIdrett,
    tittel: person?.tittel || '',
    telefon: person?.telefon || '',
    epost: person?.epost || '',
    kanHenteMotFaktura: person?.kanHenteMotFaktura || false,
    merknad: person?.merknad || '',
  });
  const [lagrer, setLagrer] = useState(false);
  const [feil, setFeil] = useState('');
  const sett = (k) => (e) => setFelt((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  async function lagre(e) {
    e.preventDefault();
    if (!felt.navn.trim()) return setFeil('Skriv inn navnet.');
    setLagrer(true);
    setFeil('');
    try {
      const body = { ...felt, sarIdrettId: felt.sarIdrettId || null };
      const svar = person
        ? await klubbApi(`/klubber/${klubb._id}/personer/${person._id}`, { method: 'PATCH', body })
        : await klubbApi(`/klubber/${klubb._id}/personer`, { method: 'POST', body });
      onLagret(svar);
    } catch (err) {
      setFeil(err.message);
      setLagrer(false);
    }
  }

  const p = person ? `pe-${person._id}` : 'pe-ny';
  return (
    <form onSubmit={lagre} className={ramme} noValidate>
      <h3 className="text-lg font-bold text-slate-900">{person ? `Endre ${person.navn}` : 'Ny person'}</h3>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Tekstfelt id={`${p}-navn`} etikett="Navn" required value={felt.navn} onChange={sett('navn')} maxLength={100} autoComplete="off" />
        <Valgfelt id={`${p}-rolle`} etikett="Rolle" value={felt.rolle} onChange={sett('rolle')}>
          {ROLLER.map((r) => <option key={r.verdi} value={r.verdi}>{r.tittel}</option>)}
        </Valgfelt>
        <Valgfelt id={`${p}-sar`} etikett="Særidrett" value={felt.sarIdrettId} onChange={sett('sarIdrettId')}>
          <option value="">Hele klubben</option>
          {klubb.sarIdretter.map((s) => <option key={s._id} value={s._id}>{s.navn}</option>)}
        </Valgfelt>
        <Tekstfelt id={`${p}-tittel`} etikett="Tittel eller lag" value={felt.tittel} onChange={sett('tittel')} maxLength={100} hjelp="F.eks. Hovedtrener G14" />
        <Tekstfelt id={`${p}-tlf`} etikett="Mobil" type="tel" inputMode="tel" value={felt.telefon} onChange={sett('telefon')} maxLength={30} autoComplete="off" />
        <Tekstfelt id={`${p}-epost`} etikett="E-post" type="email" value={felt.epost} onChange={sett('epost')} maxLength={200} autoComplete="off" />
      </div>
      <label className="flex items-center gap-2">
        <input type="checkbox" className="h-5 w-5" checked={felt.kanHenteMotFaktura} onChange={sett('kanHenteMotFaktura')} />
        Kan hente varer som faktureres klubben
      </label>
      <Tekstfelt id={`${p}-merknad`} etikett="Merknad" flerlinjet value={felt.merknad} onChange={sett('merknad')} maxLength={500} />
      <Feil>{feil}</Feil>
      <Knapper lagrer={lagrer} onAvbryt={onAvbryt} />
    </form>
  );
}

// Nytt innlegg (innlegg = null) eller endre innlegg.
// Kalkylen under varelinjene, regnet som i pristilbudet (tilbud/kalkyle.js): sum, rabatt og
// dekningsbidrag (linjer uten kostpris er ikke med i DB).
export function Kalkyle({ linjer }) {
  if (!linjer.length) return null;
  const s = oppsummering(linjer);
  return (
    <dl className="grid gap-x-4 gap-y-1 rounded-xl border border-slate-200 bg-white px-3 py-2 sm:grid-cols-2">
      <div className="flex justify-between gap-2"><dt className="text-slate-700">Rabatt</dt><dd className="font-semibold">{kr(s.rabattKr)} ({prosent(s.rabattProsent)})</dd></div>
      <div className="flex justify-between gap-2"><dt className="text-slate-700">Sum inkl. mva</dt><dd className="font-bold">{kr(s.total)}</dd></div>
      <div className="flex justify-between gap-2">
        <dt className="text-slate-700">Dekningsbidrag</dt>
        <dd className="font-semibold">{s.medKostpris ? `${kr(s.bidrag)}${s.dg != null ? ` (${prosent(s.dg)})` : ''}` : 'Ingen kostpris'}</dd>
      </div>
      {s.manglerKostpris > 0 && s.medKostpris > 0 && (
        <p className="text-sm text-amber-900 sm:col-span-2">{s.manglerKostpris} {s.manglerKostpris === 1 ? 'linje' : 'linjer'} uten kostpris er ikke med i dekningsbidraget.</p>
      )}
    </dl>
  );
}

export function InnleggSkjema({ klubb, innlegg = null, onLagret, onAvbryt }) {
  const [tittel, setTittel] = useState(innlegg?.tittel || '');
  const [tekst, setTekst] = useState(innlegg?.tekst || '');
  const [sarIdrettId, setSarIdrettId] = useState(innlegg?.sarIdrettId || '');
  const [festet, setFestet] = useState(innlegg?.festet || false);
  const [visPaStartsiden, setVisPaStartsiden] = useState(innlegg?.startside?.vises || false);
  const [type, setType] = useState(innlegg?.type || 'melding');
  // Nye innlegg er bare for leder og klubbansvarlig til de deles (vinnsel, 03.11).
  const [visForAlle, setVisForAlle] = useState(innlegg ? !innlegg.bareLedere : false);
  const [linjer, setLinjer] = useState(innlegg?.prislinjer || []);
  const bestilling = type === 'bestilling';
  // Trykk og annet arbeid fra prislisten for tekstiltrykk, som på trykksiden (vinnsel, 04.11).
  const [arbeidsliste, setArbeidsliste] = useState([]);
  useEffect(() => {
    if (!bestilling) return undefined;
    let avbrutt = false;
    getArbeidsliste('tekstiltrykk').then((l) => { if (!avbrutt) setArbeidsliste(l || []); }).catch(() => {});
    return () => { avbrutt = true; };
  }, [bestilling]);
  const [lagrer, setLagrer] = useState(false);
  const [feil, setFeil] = useState('');

  async function lagre(e) {
    e.preventDefault();
    if (!tittel.trim()) return setFeil('Skriv en overskrift.');
    setLagrer(true);
    setFeil('');
    try {
      const body = {
        tittel, tekst, sarIdrettId: sarIdrettId || null, festet, type, bareLedere: !visForAlle,
        visPaStartsiden: visForAlle && visPaStartsiden,
        ...(bestilling && { prislinjer: linjer }),
      };
      const svar = innlegg
        ? await klubbApi(`/innlegg/${innlegg._id}`, { method: 'PATCH', body })
        : await klubbApi(`/klubber/${klubb._id}/innlegg`, { method: 'POST', body });
      onLagret(svar);
    } catch (err) {
      setFeil(err.message);
      setLagrer(false);
    }
  }

  const p = innlegg ? `in-${innlegg._id}` : 'in-ny';
  return (
    <form onSubmit={lagre} className={ramme} noValidate>
      <h3 className="text-lg font-bold text-slate-900">{innlegg ? (bestilling ? 'Endre bestillingen' : 'Endre innlegget') : 'Nytt innlegg'}</h3>
      <Valgknapper
        id={`${innlegg ? `in-${innlegg._id}` : 'in-ny'}-type`}
        etikett="Hva er det?"
        valg={[{ verdi: 'melding', tittel: 'Melding' }, { verdi: 'bestilling', tittel: 'Bestilling' }]}
        verdi={type}
        onVelg={setType}
      />
      <div className="grid gap-3 sm:grid-cols-[2fr_1fr]">
        <Tekstfelt id={`${p}-tittel`} etikett="Overskrift" required value={tittel} onChange={(e) => setTittel(e.target.value)} maxLength={150} hjelp="F.eks. «Treningsdresser til G2012 kan hentes»" />
        <Valgfelt id={`${p}-sar`} etikett="Gjelder" value={sarIdrettId} onChange={(e) => setSarIdrettId(e.target.value)}>
          <option value="">Hele klubben</option>
          {klubb.sarIdretter.map((s) => <option key={s._id} value={s._id}>{s.navn}</option>)}
        </Valgfelt>
      </div>
      <Tekstfelt
        id={`${p}-tekst`}
        etikett="Tekst"
        flerlinjet
        rows={6}
        value={tekst}
        onChange={(e) => setTekst(e.target.value)}
        maxLength={10000}
        hjelp={bestilling ? 'Hvem som har bestilt, levering, faktura og annet som ikke står i varelinjene.' : 'Hva som skal hentes, av hvem, om det skal faktureres og til hvem.'}
      />
      {bestilling && (
        <fieldset className="min-w-0 space-y-2">
          <legend className="font-semibold text-slate-800">Varer og trykk</legend>
          <p className="text-sm text-slate-700">Søk i produktlisten, skann EAN, eller legg til en vare manuelt. Trykk og annet arbeid legges til under. Rabatt i kr eller %, og kalkylen regnes som i pristilbudet.</p>
          <VareLinjer idPrefiks={`${p}-v`} linjer={linjer} onEndre={setLinjer} arbeidsliste={arbeidsliste} />
          <Kalkyle linjer={linjer} />
          {/* Prisoverslag med varene slik de står nå, også før bestillingen er lagret. */}
          {linjer.length > 0 && <Prisoverslag klubb={klubb} innlegg={{ _id: innlegg?._id, tittel, sarIdrettId, prislinjer: linjer }} />}
        </fieldset>
      )}
      <label className="flex items-start gap-2">
        <input type="checkbox" className="mt-0.5 h-5 w-5" checked={festet} onChange={(e) => setFestet(e.target.checked)} />
        <span>
          Fest øverst
          <span className="block text-sm text-slate-600">For fast informasjon. Innlegg som ikke er festet slettes 24 måneder etter siste endring.</span>
        </span>
      </label>
      <label className="flex items-start gap-2">
        <input type="checkbox" className="mt-0.5 h-5 w-5" checked={visForAlle} onChange={(e) => setVisForAlle(e.target.checked)} />
        <span>
          Vis for alle ansatte
          <span className="block text-sm text-slate-600">
            Av: bare leder og klubbansvarlig ser {bestilling ? 'bestillingen' : 'meldingen'}. Huk av når de andre ansatte også skal se den{bestilling ? ', med varer og kalkyle' : ''}.
          </span>
        </span>
      </label>
      <label className={`flex items-start gap-2 ${visForAlle ? '' : 'opacity-60'}`}>
        <input type="checkbox" className="mt-0.5 h-5 w-5" checked={visForAlle && visPaStartsiden} disabled={!visForAlle} onChange={(e) => setVisPaStartsiden(e.target.checked)} />
        <span>
          Vis på kontrollpanelet
          <span className="block text-sm text-slate-600">
            {visForAlle
              ? <>Vises under «Finn kunde» for de ansatte i {butikkNavn(klubb.butikk)} til hver av dem har trykket «Merk som lest».</>
              : 'Kan bare velges når den vises for alle ansatte.'}
          </span>
        </span>
      </label>
      <Feil>{feil}</Feil>
      <Knapper lagrer={lagrer} onAvbryt={onAvbryt} lagreTekst={innlegg ? 'Lagre endringene' : 'Publiser'} />
    </form>
  );
}
